import styles from './SearchBox.module.scss';
import clsx from 'clsx';
import { searchCutIcon } from '../../../../../../../assets/hostedassets';
import SearchIcon from '../../../../../../../assets/Svg/SearchIcon';
import { DropdownOption, useSearchBoxLogic } from './useSearchBoxLogic';

// Re-exported because the animation duration is part of this component's contract, not the
// hook's — the stylesheet it mirrors is colocated here.
export { BOX_TRANSITION_MS } from './useSearchBoxLogic';

interface SearchBoxProps {
    searchTerm?: string;
    setSearchTerm?: (v: string) => void;
    onSearch?: (companyName: string) => void;
    categoryParam: string;
    location: any;
    accessTierParam: string;
    placeholder?: string;
    showDropdown?: boolean;
    dropdownOptions?: DropdownOption[];
    onOptionSelect?: (id: string) => void;
    allowResize?: boolean;
}

const SearchBox: React.FC<SearchBoxProps> = ({
    setSearchTerm,
    onSearch,
    categoryParam,
    accessTierParam,
    showDropdown = false,
    dropdownOptions = [],
    onOptionSelect,
    placeholder = 'Search by Company/Lender Name (Min 3 char)',
    allowResize = false,
}) => {
    const {
        queryText,
        isDropdownVisible,
        isExpanded,
        ownsRow,
        isCommentsResizeActive,
        searchBoxRef,
        inputRef,
        handleTextSearch,
        handleOptionSelection,
        handleExpandSearch,
        handleCollapseSearch,
    } = useSearchBoxLogic({
        setSearchTerm,
        onSearch,
        categoryParam,
        accessTierParam,
        onOptionSelect,
        showDropdown,
        dropdownOptions,
        allowResize,
    });

    return (
        <div
            // Unhashed so a surrounding layout can react to it from its own CSS module —
            // MobilePageHeader yields its title to the open input this way. Tracks `ownsRow`.
            data-expanded={ownsRow}
            className={clsx(styles.searchBox, {
                [styles.searchBoxCommentsActive]: isCommentsResizeActive,
                [styles.expanded]: ownsRow,
            })}
            ref={searchBoxRef}
        >
            <div
                className={clsx(styles.boxWrapper, {
                    [styles.expanded]: isExpanded,
                })}
                onClick={!isExpanded ? handleExpandSearch : undefined}
            >
                <input
                    ref={inputRef}
                    className={clsx(styles.searchInput, {
                        [styles.HasData]: !!queryText,
                        [styles.collapsed]: !isExpanded,
                    })}
                    type="text"
                    onChange={handleTextSearch}
                    value={queryText}
                    placeholder={isExpanded ? placeholder : ''}
                    disabled={!isExpanded}
                />
                {isExpanded ? (
                    <img alt="Cancel Icon" src={searchCutIcon} className={styles.cancelIcon} onClick={handleCollapseSearch} />
                ) : (
                    <SearchIcon
                        // Centred only once the box is a square again: while it is still closing
                        // the glyph keeps the ✕'s right inset, so the swap does not jump it.
                        // Clicks are the boxWrapper's — badged or not, they reach it from here.
                        className={clsx(styles.searchIcon, {
                            [styles.iconBadge]: !ownsRow,
                        })}
                    />
                )}
            </div>

            {showDropdown && isDropdownVisible && isExpanded && (
                <div className={styles.dropdownContainer}>
                    {dropdownOptions.length > 0 ? (
                        dropdownOptions.map((option) => (
                            <div key={option.id} className={styles.dropdownItem} onClick={() => handleOptionSelection(option.id)}>
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className={styles.dropdownItem}>No company available</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBox;
